import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable } from 'rxjs';
import { EnvironmentToken } from '../../../app.config';
import {
  Qualification,
  StoreQualificationPayload,
  TrainerOption,
  UpdateQualificationPayload,
} from '../../models/control-points/qualification.model';

@Injectable({ providedIn: 'root' })
export class QualificationService {
  #http = inject(HttpClient);
  #apiUrl = inject(EnvironmentToken).apiUrl;

  #items = new BehaviorSubject<Qualification[]>([]);
  public items$ = this.#items.asObservable();
  public items = toSignal(this.items$, { initialValue: [] });

  #trainers = new BehaviorSubject<TrainerOption[]>([]);
  public trainers$ = this.#trainers.asObservable();
  public trainers = toSignal(this.trainers$, { initialValue: [] });

  getAll() {
    this.#http
      .get<{ data: Qualification[] }>(
        `${this.#apiUrl}/api/admin/qualifications`,
      )
      .subscribe({
        next: (response) => this.#items.next(response.data ?? []),
        error: (e) => console.log(e),
      });
  }

  getById(id: number): Observable<{ data: Qualification }> {
    return this.#http.get<{ data: Qualification }>(
      `${this.#apiUrl}/api/admin/qualifications/${id}`,
    );
  }

  create(
    payload: StoreQualificationPayload,
  ): Observable<{ data: Qualification }> {
    return this.#http.post<{ data: Qualification }>(
      `${this.#apiUrl}/api/admin/qualifications`,
      payload,
    );
  }

  update(
    id: number,
    payload: UpdateQualificationPayload,
  ): Observable<{ data: Qualification }> {
    return this.#http.put<{ data: Qualification }>(
      `${this.#apiUrl}/api/admin/qualifications/${id}`,
      payload,
    );
  }

  delete(id: number): Observable<unknown> {
    return this.#http.delete(
      `${this.#apiUrl}/api/admin/qualifications/${id}`,
    );
  }

  /**
   * Loads the list of users eligible to hold a qualification (admin /
   * inspector / trainer roles). Reuses the existing role-filtered endpoint
   * — these are exactly the roles that can perform control-point check-ins.
   */
  getTrainers() {
    this.#http
      .get<{ data: TrainerOption[] }>(
        `${this.#apiUrl}/api/defects/responsible-users`,
      )
      .subscribe({
        next: (response) => this.#trainers.next(response.data ?? []),
        error: (e) => console.log(e),
      });
  }
}
